import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { Build } from '@mui/icons-material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject } from '@syncfusion/ej2-react-grids';
import { AssetListView } from '@trackly/shared';
import { LoadingIndicator, SectionPaper } from '../../../common/components/styled-ui-elements';
import { useGetAssetsQuery } from '../../../../services/AssetService/assetApi';
import { AssetEmptyState } from '../shared/states';

interface AssetsGridProps {
  searchText?: string;
  showInactive?: boolean;
  // Optional Centers filter (T072-adjacent) — when non-empty, restricts the grid to assets
  // located at one of these Centers; an empty array means "no filter" (show all), matching
  // how searchText/showInactive already treat their own empty/default values.
  selectedCenters?: string[];
  // Bulk selection (T071, User Story 4) - lets a caller (e.g. a future "Print Labels" launcher)
  // read the currently checked assets, persisted across paging via the grid's own
  // persistSelection + hidden primary-key column below.
  onSelectionChange?: (selected: AssetListView[]) => void;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  ACTV: { bg: '#d1fae5', color: '#065f46' },
  INAC: { bg: '#fee2e2', color: '#991b1b' },
  MAINT: { bg: '#fef3c7', color: '#92400e' },
  DSPS: { bg: '#e5e7eb', color: '#374151' },
  PEND: { bg: '#dbeafe', color: '#1e40af' },
};

function statusTemplate(asset: AssetListView) {
  const colors = STATUS_COLORS[asset.status] ?? { bg: '#e5e7eb', color: '#374151' };
  return (
    <span
      style={{
        display: 'inline-block',
        backgroundColor: colors.bg,
        color: colors.color,
        fontWeight: 600,
        fontSize: '0.7rem',
        padding: '2px 10px',
        borderRadius: '12px',
        lineHeight: '18px',
        whiteSpace: 'nowrap',
      }}
    >
      {asset.statusName}
    </span>
  );
}

// Checkboxes and row-click-to-navigate cannot coexist on the same click target with
// checkboxOnly selection (research.md item 8) - row navigation moves here, onto a link in the
// Name column, instead of the grid's own rowSelected event.
function NameLinkCell({ asset, onOpen }: { asset: AssetListView; onOpen: (id: string) => void }) {
  return (
    <span
      role="link"
      tabIndex={0}
      onClick={() => onOpen(asset.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(asset.id); }}
      style={{ color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline' }}
    >
      {asset.name}
    </span>
  );
}

function matchesSearch(asset: AssetListView, search: string): boolean {
  return `${asset.name} ${asset.assetTag} ${asset.categoryName} ${asset.centerName} ${asset.building} ${asset.statusName}`
    .toLowerCase()
    .includes(search);
}

interface AssetFilterCriteria {
  searchText: string;
  showInactive: boolean;
  selectedCenters: string[];
  selectedIds: Set<string>;
  showOnlySelected: boolean;
}

function filterAssets(assets: AssetListView[] | undefined, criteria: AssetFilterCriteria): AssetListView[] {
  if (!assets) return [];
  const { searchText, showInactive, selectedCenters, selectedIds, showOnlySelected } = criteria;
  let filtered = assets;
  if (!showInactive) filtered = filtered.filter((asset) => asset.status === 'ACTV');
  if (selectedCenters.length > 0) filtered = filtered.filter((asset) => selectedCenters.includes(asset.center));
  if (searchText.trim() !== '') {
    const search = searchText.toLowerCase();
    filtered = filtered.filter((asset) => matchesSearch(asset, search));
  }
  if (showOnlySelected) filtered = filtered.filter((asset) => selectedIds.has(asset.id));
  return filtered;
}

function emptyMessage(searchText: string, showOnlySelected: boolean): string {
  if (showOnlySelected) return 'No selected assets match the current filters.';
  return searchText ? 'No assets found. Try adjusting your search.' : 'No assets found. Create your first asset to get started.';
}

// Syncfusion's rowSelected/rowDeselected `args.data` is a single record for one checkbox click,
// but an array when multiple rows change at once (e.g. the header "select all" checkbox).
function normalizeSelectionData(data: unknown): AssetListView[] {
  if (Array.isArray(data)) return data as AssetListView[];
  return data ? [data as AssetListView] : [];
}

export const AssetsGrid: React.FC<AssetsGridProps> = ({ searchText = '', showInactive = false, selectedCenters = [], onSelectionChange }) => {
  const navigate = useNavigate();
  const { data: assets } = useGetAssetsQuery({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  const filteredAssets = useMemo(
    () => filterAssets(assets, { searchText, showInactive, selectedCenters, selectedIds, showOnlySelected }),
    [assets, searchText, showInactive, selectedCenters, selectedIds, showOnlySelected]
  );

  const applySelection = (nextIds: Set<string>) => {
    setSelectedIds(nextIds);
    onSelectionChange?.(assets?.filter((asset) => nextIds.has(asset.id)) ?? []);
  };

  const handleRowSelected = (args: any) => {
    const next = new Set(selectedIds);
    normalizeSelectionData(args?.data).forEach((asset) => next.add(asset.id));
    applySelection(next);
  };

  const handleRowDeselected = (args: any) => {
    const next = new Set(selectedIds);
    normalizeSelectionData(args?.data).forEach((asset) => next.delete(asset.id));
    applySelection(next);
  };

  const openAsset = (id: string) => navigate(`/assets/edit?id=${id}`);

  if (!assets) {
    return (
      <SectionPaper>
        <LoadingIndicator icon={Build} message="Loading assets..." />
      </SectionPaper>
    );
  }

  return (
    <SectionPaper>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
          {`${selectedIds.size} of ${assets.length} selected \u00b7 Showing ${filteredAssets.length} asset${filteredAssets.length !== 1 ? 's' : ''}`}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showOnlySelected}
              onChange={(e) => setShowOnlySelected(e.target.checked)}
            />
          }
          label={<Typography variant="body2" sx={{ color: '#64748b' }}>Show only selected</Typography>}
        />
      </Box>

      {filteredAssets.length === 0 ? (
        <AssetEmptyState message={emptyMessage(searchText, showOnlySelected)} />
      ) : (
        <GridComponent
          dataSource={filteredAssets}
          allowPaging={true}
          allowSorting={true}
          pageSettings={{ pageSize: 20, pageSizes: [10, 20, 48, 100] }}
          sortSettings={{
            columns: [{ field: 'name', direction: 'Ascending' as any }],
          }}
          height={500}
          rowHeight={36}
          gridLines="Both"
          selectionSettings={{ type: 'Multiple', checkboxOnly: true, persistSelection: true }}
          rowSelected={handleRowSelected}
          rowDeselected={handleRowDeselected}
        >
          <ColumnsDirective>
            <ColumnDirective type="checkbox" width="50" />
            <ColumnDirective field="id" isPrimaryKey={true} visible={false} />
            <ColumnDirective
              field="name"
              headerText="Name"
              width="160"
              textAlign="Left"
              template={(asset: AssetListView) => <NameLinkCell asset={asset} onOpen={openAsset} />}
            />
            <ColumnDirective field="assetTag" headerText="Tag" width="100" textAlign="Left" />
            <ColumnDirective field="categoryName" headerText="Category" width="110" textAlign="Left" />
            <ColumnDirective field="centerName" headerText="Center" width="100" textAlign="Left" />
            <ColumnDirective field="building" headerText="Building" width="100" textAlign="Left" />
            <ColumnDirective headerText="Status" width="90" textAlign="Center" template={statusTemplate} />
          </ColumnsDirective>
          <Inject services={[Page, Sort]} />
        </GridComponent>
      )}
    </SectionPaper>
  );
};

export default AssetsGrid;